import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ScaleIcon, ExclamationTriangleIcon, CheckCircleIcon, ClockIcon } from '@heroicons/react/24/outline';
import { useBusinessTypeContext } from '../../contexts/BusinessTypeContext';
import { useLocation } from '../../contexts/LocationContext';
import { useApi, useMutation } from '../../hooks/useApi';
import { apiClient, getErrorMessage } from '../../utils/api';
import { GoatMaster, GoatWeightLog, WeightRecommendation } from '../../types/api';
import LoadingSpinner from '../../components/UI/LoadingSpinner';

const GoatWeightRegister: React.FC = () => {
  const { businessType } = useBusinessTypeContext();
  const { selectedLocation } = useLocation();
  const [activeTab, setActiveTab] = useState<'record' | 'recommendations' | 'history'>('recommendations');
  const [selectedTag, setSelectedTag] = useState<string>('');
  const [weightDate, setWeightDate] = useState<string>(new Date().toISOString().split('T')[0]);
  const [weightKg, setWeightKg] = useState<string>('');
  const [ageMilestone, setAgeMilestone] = useState<string>('');
  const [remarks, setRemarks] = useState<string>('');
  const [error, setError] = useState<string>('');
  const [success, setSuccess] = useState<string>('');
  const [selectedYear, setSelectedYear] = useState<number>(new Date().getFullYear());

  const { mutate: createWeightLog, loading: creating } = useMutation<GoatWeightLog>();
  const { data: goats, loading: loadingGoats, refetch: refetchGoats } = useApi<GoatMaster[]>(
    `/goat/master?location_id=${selectedLocation?.id}`,
    { skip: !selectedLocation?.id, dependencies: [selectedLocation?.id] }
  );
  const { data: recommendations, loading: loadingRecommendations, refetch: refetchRecommendations } = useApi<WeightRecommendation[]>(
    `/goat/weight-logs/recommendations?location_id=${selectedLocation?.id}`,
    { skip: !selectedLocation?.id, dependencies: [selectedLocation?.id] }
  );
  const { data: weightLogs, loading: loadingLogs, refetch: refetchLogs } = useApi<GoatWeightLog[]>(
    `/goat/weight-logs?location_id=${selectedLocation?.id}`,
    { skip: !selectedLocation?.id, dependencies: [selectedLocation?.id] }
  );

  useEffect(() => {
    if (selectedLocation?.id) {
      refetchGoats();
      refetchRecommendations();
      refetchLogs();
    }
  }, [selectedLocation?.id]);

  // Auto-calculate milestone when goat or weight date changes
  useEffect(() => {
    const calculateMilestone = async () => {
      if (!selectedTag || !weightDate || !selectedLocation?.id) {
        setAgeMilestone('');
        return;
      }

      try {
        const response = await apiClient.get(
          `/goat/weight-logs/current-milestone?location_id=${selectedLocation.id}&tag=${selectedTag}&reference_date=${weightDate}`
        );
        if (response.data?.milestone) {
          setAgeMilestone(response.data.milestone);
        } else {
          setAgeMilestone('');
        }
      } catch (err) {
        console.error('Failed to calculate milestone:', err);
        setAgeMilestone('');
      }
    };

    calculateMilestone();
  }, [selectedTag, weightDate, selectedLocation?.id]);

  const handleRecordWeight = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (!selectedTag || !weightDate || !weightKg) {
      setError('Please fill in all required fields');
      return;
    }

    try {
      await createWeightLog('/goat/weight-logs', {
        location_id: selectedLocation?.id,
        tag: selectedTag,
        weight_date: weightDate,
        weight_kg: parseFloat(weightKg),
        age_milestone: ageMilestone || undefined,
        remarks: remarks || undefined,
      });

      setSuccess('Weight recorded successfully!');
      // Reset form (keep selectedTag and weightDate for potential next entry)
      setWeightKg('');
      setRemarks('');
      // Note: ageMilestone will be auto-calculated via useEffect
      // Refresh data
      refetchRecommendations();
      refetchLogs();
      
      // Clear success message after 3 seconds
      setTimeout(() => {
        setSuccess('');
      }, 3000);
    } catch (err: any) {
      setError(getErrorMessage(err, 'Failed to record weight'));
    }
  };

  const handleQuickRecord = async (recommendation: WeightRecommendation) => {
    setSelectedTag(recommendation.tag);
    setWeightDate(new Date().toISOString().split('T')[0]);
    // Milestone will be auto-calculated via useEffect
    setActiveTab('record');
    // Scroll to form
    setTimeout(() => {
      document.getElementById('weight-form')?.scrollIntoView({ behavior: 'smooth' });
    }, 100);
  };

  const getMilestoneDisplay = (milestone: string) => {
    const milestoneMap: Record<string, string> = {
      'Birth Weight': 'Birth Weight',
      '15 day': '15 days',
      '30 day': '30 days (1M)',
      '45 day': '45 days',
      '60 day': '60 days (2M)',
      '75 day': '75 days',
      '90 day': '90 days (3M) ⚠️',
      '4M': '4 months',
      '5M': '5 months',
      '6M': '6 months',
      '7M': '7 months',
      '8M': '8 months',
      '9M': '9 months',
      '10M': '10 months',
      '11M': '11 months',
      '12M': '12 months',
      '2Y': '2 years',
      '3Y': '3 years',
      '4Y': '4 years',
      '5Y': '5 years',
    };
    return milestoneMap[milestone] || milestone;
  };

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric' 
    });
  };

  const getGoatName = (tag: string) => {
    const goat = goats?.find(g => g.tag_no === tag);
    return goat ? `${goat.tag_no}${goat.breed ? ` (${goat.breed})` : ''}` : tag;
  };

  const formatAge = (days: number): string => {
    if (days < 30) {
      return `${days} day${days !== 1 ? 's' : ''}`;
    }
    
    if (days < 365) {
      // Less than 12 months: show in months (can include days)
      const months = Math.floor(days / 30);
      const remainingDays = days % 30;
      
      if (remainingDays === 0) {
        return `${months}M`;
      } else {
        return `${months}M ${remainingDays}D`;
      }
    }
    
    // 12+ months: show in years with months/days
    const years = Math.floor(days / 365);
    const remainingDaysAfterYears = days % 365;
    const months = Math.floor(remainingDaysAfterYears / 30);
    const remainingDays = remainingDaysAfterYears % 30;
    
    const parts: string[] = [];
    if (years > 0) {
      parts.push(`${years} Y`);
    }
    if (months > 0) {
      parts.push(`${months}M`);
    }
    if (remainingDays > 0) {
      parts.push(`${remainingDays}D`);
    }
    
    return parts.join(' ') || '0 days';
  };

  // Calculate total weights by category
  const calculateWeightTotals = () => {
    if (!goats) return { doe: 0, buck: 0, kid: 0 };

    let doeTotal = 0;
    let buckTotal = 0;
    let kidTotal = 0;

    goats.forEach((goat) => {
      // Only count active goats with latest weight
      if (goat.status !== 'active' || !goat.latest_weight_kg) return;

      if (goat.category === 'doe') {
        doeTotal += goat.latest_weight_kg;
      } else if (goat.category === 'buck') {
        // Exclude breeder bucks
        if (!goat.is_breeder) {
          buckTotal += goat.latest_weight_kg;
        }
      } else if (goat.category === 'kid') {
        kidTotal += goat.latest_weight_kg;
      }
    });

    return { doe: doeTotal, buck: buckTotal, kid: kidTotal };
  };

  const weightTotals = calculateWeightTotals();

  // Get available years from weight logs
  const getAvailableYears = (): number[] => {
    if (!weightLogs || weightLogs.length === 0) return [new Date().getFullYear()];
    
    const years = new Set<number>();
    weightLogs.forEach((log) => {
      const logYear = new Date(log.weight_date).getFullYear();
      years.add(logYear);
    });
    
    return Array.from(years).sort((a, b) => b - a); // Sort descending (newest first)
  };

  // Filter weight logs by selected year
  const filteredWeightLogs = weightLogs?.filter((log) => {
    const logYear = new Date(log.weight_date).getFullYear();
    return logYear === selectedYear;
  }) || [];

  if (businessType !== 'goat') {
    return (
      <div className="container-mobile py-8 text-center">
        <p className="text-gray-600">This page is only available for goat farming locations.</p>
        <Link to="/dashboard" className="text-blue-600 hover:text-blue-800">Go to Dashboard</Link>
      </div>
    );
  }

  if (!selectedLocation) {
    return (
      <div className="container-mobile py-8 text-center">
        <p className="text-gray-600">Please select a location first</p>
        <Link to="/locations" className="text-blue-600 hover:text-blue-800">Go to Location Selection</Link>
      </div>
    );
  }

  const criticalRecommendations = recommendations?.filter(r => r.is_critical) || [];
  const otherRecommendations = recommendations?.filter(r => !r.is_critical) || [];
  const activeGoats = goats?.filter(g => g.status === 'active') || [];

  return (
    <div className="container-mobile py-6 space-y-6">
      {/* Header */}
      <div className="text-center">
        <ScaleIcon className="h-16 w-16 text-blue-600 mx-auto mb-4" />
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Weight Register</h1>
        <p className="text-lg text-gray-600">
          Track and record goat weight measurements over time
        </p>
      </div>

      {/* Tabs */}
      <div className="flex space-x-2 border-b border-gray-200">
        <button
          onClick={() => setActiveTab('recommendations')}
          className={`px-4 py-2 font-medium transition-colors ${
            activeTab === 'recommendations'
              ? 'border-b-2 border-blue-600 text-blue-600'
              : 'text-gray-600 hover:text-gray-900'
          }`}
        >
          Recommendations
          {recommendations && recommendations.length > 0 && (
            <span className="ml-2 px-2 py-0.5 text-xs bg-red-100 text-red-800 rounded-full">
              {recommendations.length}
            </span>
          )}
        </button>
        <button
          onClick={() => setActiveTab('record')}
          className={`px-4 py-2 font-medium transition-colors ${
            activeTab === 'record'
              ? 'border-b-2 border-blue-600 text-blue-600'
              : 'text-gray-600 hover:text-gray-900'
          }`}
        >
          Record Weight
        </button>
        <button
          onClick={() => setActiveTab('history')}
          className={`px-4 py-2 font-medium transition-colors ${
            activeTab === 'history'
              ? 'border-b-2 border-blue-600 text-blue-600'
              : 'text-gray-600 hover:text-gray-900'
          }`}
        >
          Weight History
        </button>
      </div>

      {/* Recommendations Tab */}
      {activeTab === 'recommendations' && (
        <div className="space-y-6">
          {loadingRecommendations ? (
            <div className="text-center py-8">
              <LoadingSpinner />
            </div>
          ) : (
            <>
              {/* Critical Recommendations (3M) */}
              {criticalRecommendations.length > 0 && (
                <div className="card p-6 border-2 border-red-500 bg-red-50">
                  <div className="flex items-center mb-4">
                    <ExclamationTriangleIcon className="h-6 w-6 text-red-600 mr-2" />
                    <h2 className="text-xl font-bold text-red-900">Critical: 3M Weight Recording Required</h2>
                  </div>
                  <p className="text-red-800 mb-4">
                    These goats have reached or passed the critical 3-month milestone. Please record their weight immediately.
                  </p>
                  <div className="space-y-3">
                    {criticalRecommendations.map((rec) => (
                      <div
                        key={`${rec.tag}-${rec.milestone}`}
                        className="bg-white p-4 rounded-lg border border-red-300 flex items-center justify-between"
                      >
                        <div className="flex-1">
                          <div className="font-semibold text-gray-900">{getGoatName(rec.tag)}</div>
                          <div className="text-sm text-gray-600">
                            Milestone: <span className="font-medium">{getMilestoneDisplay(rec.milestone)}</span>
                          </div>
                          <div className="text-sm text-gray-600">
                            Due: {formatDate(rec.due_date)}
                            {rec.days_overdue > 0 && (
                              <span className="ml-2 text-red-600 font-medium">
                                ({rec.days_overdue} days overdue)
                              </span>
                            )}
                          </div>
                          {rec.current_age_days !== undefined && (
                            <div className="text-xs text-gray-500 mt-1">
                              Current age: {formatAge(rec.current_age_days)}
                            </div>
                          )}
                        </div>
                        <button
                          onClick={() => handleQuickRecord(rec)}
                          className="ml-4 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors text-sm font-medium"
                        >
                          Record Now
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Other Recommendations */}
              {otherRecommendations.length > 0 && (
                <div className="card p-6">
                  <div className="flex items-center mb-4">
                    <ClockIcon className="h-6 w-6 text-blue-600 mr-2" />
                    <h2 className="text-xl font-bold text-gray-900">Pending Weight Recordings</h2>
                  </div>
                  <div className="space-y-3">
                    {otherRecommendations.map((rec) => (
                      <div
                        key={`${rec.tag}-${rec.milestone}`}
                        className="bg-gray-50 p-4 rounded-lg border border-gray-200 flex items-center justify-between hover:bg-gray-100 transition-colors"
                      >
                        <div className="flex-1">
                          <div className="font-semibold text-gray-900">{getGoatName(rec.tag)}</div>
                          <div className="text-sm text-gray-600">
                            Milestone: <span className="font-medium">{getMilestoneDisplay(rec.milestone)}</span>
                          </div>
                          <div className="text-sm text-gray-600">
                            Due: {formatDate(rec.due_date)}
                            {rec.days_overdue > 0 && (
                              <span className="ml-2 text-orange-600 font-medium">
                                ({rec.days_overdue} days overdue)
                              </span>
                            )}
                            {rec.days_overdue === 0 && (
                              <span className="ml-2 text-green-600 font-medium">Due today</span>
                            )}
                            {rec.days_overdue < 0 && (
                              <span className="ml-2 text-blue-600 font-medium">
                                (Due in {Math.abs(rec.days_overdue)} days)
                              </span>
                            )}
                          </div>
                          {rec.current_age_days !== undefined && (
                            <div className="text-xs text-gray-500 mt-1">
                              Current age: {formatAge(rec.current_age_days)}
                            </div>
                          )}
                        </div>
                        <button
                          onClick={() => handleQuickRecord(rec)}
                          className="ml-4 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium"
                        >
                          Record
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* No Recommendations */}
              {recommendations && recommendations.length === 0 && (
                <div className="card p-8 text-center">
                  <CheckCircleIcon className="h-12 w-12 text-green-500 mx-auto mb-4" />
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">All Up to Date!</h3>
                  <p className="text-gray-600">
                    There are no pending weight recordings at this time.
                  </p>
                </div>
              )}
            </>
          )}
        </div>
      )}

      {/* Record Weight Tab */}
      {activeTab === 'record' && (
        <div className="card p-6">
          <h2 className="text-xl font-bold text-gray-900 mb-6">Record Weight</h2>
          
          {error && (
            <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg text-red-800">
              {error}
            </div>
          )}
          
          {success && (
            <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded-lg text-green-800">
              {success}
            </div>
          )}

          <form id="weight-form" onSubmit={handleRecordWeight} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Goat Tag <span className="text-red-500">*</span>
              </label>
              <select
                value={selectedTag}
                onChange={(e) => setSelectedTag(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                required
              >
                <option value="">Select a goat</option>
                {activeGoats.map((goat) => (
                  <option key={goat.id} value={goat.tag_no}>
                    {goat.tag_no}{goat.breed ? ` (${goat.breed})` : ''}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Weight Date <span className="text-red-500">*</span>
              </label>
              <input
                type="date"
                value={weightDate}
                onChange={(e) => setWeightDate(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Weight (kg) <span className="text-red-500">*</span>
              </label>
              <input
                type="number"
                step="0.1"
                min="0"
                value={weightKg}
                onChange={(e) => setWeightKg(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="e.g., 25.5"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Age Milestone <span className="text-gray-500 text-xs">(Auto-calculated)</span>
              </label>
              <input
                type="text"
                value={ageMilestone ? getMilestoneDisplay(ageMilestone) : 'Calculating...'}
                readOnly
                className="w-full px-4 py-2 border border-gray-300 rounded-lg bg-gray-50 text-gray-700 cursor-not-allowed"
                placeholder={selectedTag ? 'Select goat and date to calculate milestone' : 'Select a goat first'}
              />
              {!selectedTag && (
                <p className="mt-1 text-xs text-gray-500">
                  Milestone will be automatically calculated based on the selected goat's age
                </p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Remarks (Optional)
              </label>
              <textarea
                value={remarks}
                onChange={(e) => setRemarks(e.target.value)}
                rows={3}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="Any additional notes..."
              />
            </div>

            <div className="flex space-x-4 pt-4">
              <button
                type="submit"
                disabled={creating}
                className="flex-1 btn-touch bg-blue-600 text-white py-3 rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center justify-center"
              >
                {creating ? <LoadingSpinner size="sm" /> : 'Record Weight'}
              </button>
              <button
                type="button"
                onClick={() => {
                  setSelectedTag('');
                  setWeightKg('');
                  setRemarks('');
                  setError('');
                  setSuccess('');
                  // Note: ageMilestone will be cleared automatically when selectedTag is cleared
                }}
                className="px-6 py-3 border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Clear
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Weight History Tab */}
      {activeTab === 'history' && (
        <div className="space-y-6">
          {/* Weight Summary */}
          {goats && goats.length > 0 && (
            <div className="card p-6">
              <h2 className="text-xl font-bold text-gray-900 mb-4">Total Weight Summary</h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                  <div className="text-sm font-medium text-blue-800 mb-1">Does</div>
                  <div className="text-2xl font-bold text-blue-900">
                    {weightTotals.doe.toFixed(1)} kg
                  </div>
                  <div className="text-xs text-blue-600 mt-1">
                    {goats.filter(g => g.category === 'doe' && g.status === 'active' && g.latest_weight_kg).length} active
                  </div>
                </div>
                <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                  <div className="text-sm font-medium text-green-800 mb-1">Bucks (Non-Breeder)</div>
                  <div className="text-2xl font-bold text-green-900">
                    {weightTotals.buck.toFixed(1)} kg
                  </div>
                  <div className="text-xs text-green-600 mt-1">
                    {goats.filter(g => g.category === 'buck' && g.status === 'active' && !g.is_breeder && g.latest_weight_kg).length} active
                  </div>
                </div>
                <div className="bg-purple-50 p-4 rounded-lg border border-purple-200">
                  <div className="text-sm font-medium text-purple-800 mb-1">Kids</div>
                  <div className="text-2xl font-bold text-purple-900">
                    {weightTotals.kid.toFixed(1)} kg
                  </div>
                  <div className="text-xs text-purple-600 mt-1">
                    {goats.filter(g => g.category === 'kid' && g.status === 'active' && g.latest_weight_kg).length} active
                  </div>
                </div>
              </div>
              <div className="mt-4 pt-4 border-t border-gray-200">
                <div className="flex justify-between items-center">
                  <span className="text-sm font-medium text-gray-700">Grand Total:</span>
                  <span className="text-lg font-bold text-gray-900">
                    {(weightTotals.doe + weightTotals.buck + weightTotals.kid).toFixed(1)} kg
                  </span>
                </div>
              </div>
            </div>
          )}

          {loadingLogs ? (
            <div className="text-center py-8">
              <LoadingSpinner />
            </div>
          ) : weightLogs && weightLogs.length > 0 ? (
            <div className="card p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-bold text-gray-900">Weight History</h2>
                <div className="flex items-center space-x-2">
                  <label htmlFor="year-filter" className="text-sm font-medium text-gray-700">
                    Year:
                  </label>
                  <select
                    id="year-filter"
                    value={selectedYear}
                    onChange={(e) => setSelectedYear(parseInt(e.target.value))}
                    className="px-3 py-1.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                  >
                    {getAvailableYears().map((year) => (
                      <option key={year} value={year}>
                        {year}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              {filteredWeightLogs.length > 0 ? (
                <div className="space-y-4">
                  {filteredWeightLogs.map((log) => (
                  <div
                    key={log.id}
                    className="bg-gray-50 p-4 rounded-lg border border-gray-200"
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="font-semibold text-gray-900">{getGoatName(log.tag)}</div>
                        <div className="text-sm text-gray-600 mt-1">
                          <span className="font-medium">{log.weight_kg} kg</span>
                          {' • '}
                          <span>{formatDate(log.weight_date)}</span>
                          {log.age_milestone && (
                            <>
                              {' • '}
                              <span className="text-blue-600">{getMilestoneDisplay(log.age_milestone)}</span>
                            </>
                          )}
                        </div>
                        {log.remarks && (
                          <div className="text-sm text-gray-500 mt-2 italic">{log.remarks}</div>
                        )}
                      </div>
                    </div>
                  </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-gray-500">
                  <p>No weight records found for {selectedYear}</p>
                  <p className="text-sm mt-2">Try selecting a different year</p>
                </div>
              )}
            </div>
          ) : (
            <div className="card p-8 text-center">
              <ScaleIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-gray-900 mb-2">No Weight Records Yet</h3>
              <p className="text-gray-600 mb-4">
                Start recording weights to track your goats' growth over time.
              </p>
              <button
                onClick={() => setActiveTab('record')}
                className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                Record First Weight
              </button>
            </div>
          )}
        </div>
      )}

      {/* Navigation Links */}
      <div className="flex justify-center space-x-4 pt-4">
        <Link
          to="/goat/logs"
          className="px-6 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
        >
          Back to Goat Logs
        </Link>
        <Link
          to="/goat/dashboard"
          className="px-6 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
        >
          Go to Dashboard
        </Link>
      </div>
    </div>
  );
};

export default GoatWeightRegister;
